import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  clearAiResourceListPrefetchPreference,
  clearAiProviderSettings,
  getAiAutoFunctionInvocationPolicy,
  getAiKernelFunctionPolicy,
  getAiPromptConfig,
  getAiPromptVersionHistory,
  getAiProviderSettings,
  getAiResourceListPrefetchPreference,
  getAiUseCasePromptConfig,
  getAiUseCasePromptVersionHistory,
  getAiExternalChannelOverrides,
  resetAiPromptToDefault,
  resetAiUseCasePromptConfig,
  revertAiPromptToVersion,
  clearAiExternalChannelOverride,
  updateAiAutoFunctionInvocationPolicy,
  updateAiKernelFunctionPolicy,
  updateAiKernelPluginInstruction,
  updateAiToolHistoryInclusion,
  updateAiPromptConfig,
  updateAiUseCasePromptConfig,
  updateAiProviderSettings,
  updateAiResourceListPrefetchPreference,
} from '../services/aiPromptConfigService';
import {
  AI_CHAT_PROVIDER_OPTIONS,
  AI_EXTERNAL_CHANNEL_RAIL_ITEMS,
  AI_INSTAGRAM_COMMENT_USE_CASE,
  AI_MESSENGER_COMMENT_USE_CASE,
  AI_KERNEL_PLUGIN_INSTRUCTION_MAX_LENGTH,
  AI_PROMPT_USE_CASE_TABS,
  AI_SETTINGS_GENERAL_TAB,
  AiPromptUseCase,
  isChannelSpecificExternalUnmutedUseCase,
  isExternalUnmutedUseCase,
  usesDedicatedPromptOverride,
  TONE_OPTIONS,
} from '../types/AiPromptConfig';
import type {
  AiChatProviderOption,
  AiExternalChannelOverrideStatus,
  AiPromptConfig,
  AiPromptTemplateVariable,
  AiPromptUseCaseId,
  AiPromptUseCaseTabConfig,
  AiPromptVersion,
  AiProviderSetting,
  AiProviderSettings,
  AiProviderId,
  AiSettingsTabId,
  AiKernelFunctionSetting,
  AiKernelFunctionToolPlugin,
  ExternalChannelRailItem,
  ExternalChannelRailItemId,
  UpdateAiPromptConfigRequest,
  UpdateAiProviderSettingsRequest,
} from '../types/AiPromptConfig';
import styles from './AiPromptConfigPage.module.css';
import { AiProviderConfigurationPanel } from './AiProviderConfigurationPanel';
import { AiAttachmentPolicyEditor } from './AiAttachmentPolicyEditor';
import ConversationChannelBadge from '../../conversations/components/ConversationChannelBadge';
import { Spinner } from '../../../shared/components';
import {
  buildCustomFieldsValue,
  createDefaultAttachmentPolicyDraft,
  parseAttachmentPolicyDraft,
  type AttachmentPolicyDraft,
} from '../utils/attachmentPolicyModel';

const resourceToolFunctionId = 'knowledge.resource.tools';
const filesReadToolsFunctionId = 'files.read.tools';
const filesShareToolsFunctionId = 'files.share.tools';

const CUSTOMER_FACING_REPLY_USE_CASES = new Set<string>([
  AiPromptUseCase.ReplyInstagramComment,
  AiPromptUseCase.ReplyMessengerComment,
]);

const getKernelFunctionDescriptionKey = (functionId: string): string | null => {
  switch (functionId) {
    case filesReadToolsFunctionId:
      return 'aiSettings.kernelFunctions.filesReadToolsDescription';
    case filesShareToolsFunctionId:
      return 'aiSettings.kernelFunctions.filesShareToolsDescription';
    default:
      return null;
  }
};

const isCustomerFacingReplyUseCase = (useCaseId: string): boolean =>
  CUSTOMER_FACING_REPLY_USE_CASES.has(useCaseId) || isExternalUnmutedUseCase(useCaseId);

const buildInstructionDrafts = (functions: AiKernelFunctionSetting[]): Record<string, string> =>
  functions.reduce<Record<string, string>>((drafts, item) => {
    if (item.supportsInstruction) {
      drafts[item.functionId] = item.instruction ?? '';
    }
    return drafts;
  }, {});

interface ToolPolicyPluginMember {
  tool: AiKernelFunctionSetting;
  isOwned: boolean;
  ownerDisplayName: string;
}

interface ToolPolicyPluginGroup {
  pluginId: string;
  displayName: string;
  kernelPluginName: string;
  isPublished: boolean;
  members: ToolPolicyPluginMember[];
}

interface ToolPolicyGrouping {
  pluginGroups: ToolPolicyPluginGroup[];
  ungroupedTools: AiKernelFunctionSetting[];
}

const groupToolFunctionsByPlugin = (
  functions: AiKernelFunctionSetting[],
  plugins: AiKernelFunctionToolPlugin[] = [],
): ToolPolicyGrouping => {
  const groups = new Map<string, ToolPolicyPluginGroup>();
  const ungroupedTools: AiKernelFunctionSetting[] = [];

  plugins.forEach((plugin) => {
    if (!plugin.pluginId || groups.has(plugin.pluginId)) {
      return;
    }

    groups.set(plugin.pluginId, { ...plugin, members: [] });
  });

  functions.forEach((item) => {
    // Plugin order within a function is backend storage order, so the owner of a shared tool is arbitrary.
    const owner = item.toolPlugins[0];
    if (!owner) {
      ungroupedTools.push(item);
      return;
    }

    const seenPluginIds = new Set<string>();
    item.toolPlugins.forEach((plugin) => {
      if (seenPluginIds.has(plugin.pluginId)) {
        return;
      }
      seenPluginIds.add(plugin.pluginId);

      let group = groups.get(plugin.pluginId);
      if (!group) {
        group = { ...plugin, members: [] };
        groups.set(plugin.pluginId, group);
      }

      group.members.push({
        tool: item,
        isOwned: plugin.pluginId === owner.pluginId,
        ownerDisplayName: owner.displayName,
      });
    });
  });

  return {
    pluginGroups: Array.from(groups.values()).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    ungroupedTools,
  };
};

const resourceListInstructionFieldName = 'resourceListInstruction';

const knowledgeToolGuidanceExample = `{
  "resourceListInstruction": "When knowledge resources are listed, prefer the most relevant snippets first, cite the matching resource ids when useful, and ask the user a clarifying question instead of guessing when the retrieved material is incomplete."
}`;

const getProviderTranslationKey = (id: string): string => {
  switch (id) {
    case 'AzureOpenAI':
      return 'aiSettings.providers.azureOpenAi';
    case 'OpenAI':
      return 'aiSettings.providers.openAi';
    case 'DeepSeek':
      return 'aiSettings.providers.deepSeek';
    case 'Anthropic':
      return 'aiSettings.providers.anthropic';
    case 'GoogleGemini':
      return 'aiSettings.providers.googleGemini';
    case 'MistralAI':
      return 'aiSettings.providers.mistralAi';
    default:
      return 'aiSettings.providers.openAi';
  }
};

const getChatProviderTranslationKey = (value: AiChatProviderOption): string => {
  switch (value) {
    case 'AzureOpenAI':
      return 'aiSettings.chatProviders.azureOpenAi';
    case 'OpenAI':
      return 'aiSettings.chatProviders.openAi';
    case 'DeepSeek':
      return 'aiSettings.chatProviders.deepSeek';
    case 'Anthropic':
      return 'aiSettings.chatProviders.anthropic';
    case 'GoogleGemini':
      return 'aiSettings.chatProviders.googleGemini';
    case 'MistralAI':
      return 'aiSettings.chatProviders.mistralAi';
    default:
      return 'aiSettings.chatProviders.auto';
  }
};

const buildProviderDraft = (settings: AiProviderSettings): UpdateAiProviderSettingsRequest => {
  const findProvider = (id: string): AiProviderSetting | undefined =>
    settings.providers.find((provider) => provider.id === id);

  return {
    chatProvider: settings.chatProvider,
    azureOpenAiEnabled: findProvider('AzureOpenAI')?.enabled ?? false,
    openAiEnabled: findProvider('OpenAI')?.enabled ?? false,
    deepSeekEnabled: findProvider('DeepSeek')?.enabled ?? false,
    anthropicEnabled: findProvider('Anthropic')?.enabled ?? false,
    googleGeminiEnabled: findProvider('GoogleGemini')?.enabled ?? false,
    mistralAiEnabled: findProvider('MistralAI')?.enabled ?? false,
  };
};

const getUseCaseTranslationPrefix = (id: AiPromptUseCaseId): string => {
  switch (id) {
    case 'conversation.reply':
      return 'aiSettings.useCases.reply';
    case 'conversation.reply.mention':
      return 'aiSettings.useCases.replyMention';
    case 'conversation.reply.instagram.comment':
      return 'aiSettings.useCases.instagramComment';
    case 'conversation.reply.messenger.comment':
      return 'aiSettings.useCases.messengerComment';
    case 'conversation.draft':
      return 'aiSettings.useCases.draft';
    case 'conversation.summary':
      return 'aiSettings.useCases.summary';
    default:
      if (isExternalUnmutedUseCase(id)) {
        return 'aiSettings.useCases.externalUnmuted';
      }
      return 'aiSettings.useCases.reply';
  }
};

const getToneTranslationKey = (value: string): string => `aiSettings.toneOptions.${value}`;

const tryParseCustomFieldsObject = (value: string | null | undefined): Record<string, unknown> | null => {
  if (!value?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

const parseCustomFieldsObject = (value: string | null | undefined): Record<string, unknown> => {
  return tryParseCustomFieldsObject(value) ?? {};
};

const getResourceListInstruction = (value: string | null | undefined): string => {
  const parsed = parseCustomFieldsObject(value);
  const instruction = parsed[resourceListInstructionFieldName];
  return typeof instruction === 'string' ? instruction : '';
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

type UseCaseSubSection = 'prompt' | 'tools' | 'advanced';

const USE_CASE_SUB_SECTIONS: UseCaseSubSection[] = ['prompt', 'tools', 'advanced'];

interface PromptEditorTabProps {
  useCase: AiPromptUseCaseTabConfig;
  canManage: boolean;
  onOverrideStateChange?: () => void;
}

interface PromptVariablesPanelProps {
  variables: AiPromptTemplateVariable[];
}

interface SectionHeaderProps {
  title: string;
  description?: string;
  exampleJson?: string;
  exampleLabel?: string;
  isExampleOpen?: boolean;
  onToggleExample?: () => void;
  exampleButtonId?: string;
  examplePanelId?: string;
  actions?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  exampleJson,
  exampleLabel,
  isExampleOpen = false,
  onToggleExample,
  exampleButtonId,
  examplePanelId,
  actions,
}) => {
  const { t } = useTranslation(['admin']);
  const hasExample = Boolean(exampleJson?.trim()) && Boolean(onToggleExample);

  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionHeaderMain}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {description ? <p className={styles.sectionDescription}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.sectionHeaderActions}>{actions}</div> : null}
      {hasExample ? (
        <button
          id={exampleButtonId}
          type="button"
          className={styles.sectionInfoButton}
          onClick={onToggleExample}
          aria-expanded={isExampleOpen}
          aria-controls={examplePanelId}
          title={exampleLabel || t('aiSettings.attachmentPolicyExampleLabel', { ns: 'admin' })}
        >
          i
        </button>
      ) : null}
      {hasExample && isExampleOpen ? (
        <div id={examplePanelId} className={styles.sectionExamplePanel}>
          <div className={styles.sectionExampleLabel}>{exampleLabel || t('aiSettings.exampleLabel', { ns: 'admin' })}</div>
          <pre className={styles.sectionExampleCode}>{exampleJson}</pre>
        </div>
      ) : null}
    </div>
  );
};

const PromptVariablesPanel: React.FC<PromptVariablesPanelProps> = ({ variables }) => {
  const { t } = useTranslation(['admin']);

  if (variables.length === 0) {
    return null;
  }

  return (
    <div className={styles.variablePanel}>
      <div className={styles.variablePanelHeader}>{t('aiSettings.liquidVariablesTitle', { ns: 'admin' })}</div>
      <p className={styles.variablePanelText}>
        {t('aiSettings.liquidVariablesDescription', { ns: 'admin' })}
      </p>
      <div className={styles.variableList}>
        {variables.map((item) => (
          <div key={item.name} className={styles.variableCard}>
            <div className={styles.variableToken}>{`{{${item.name}}}`}</div>
            <div className={styles.variableDescription}>{item.description}</div>
            <div className={styles.variableExample}>{t('aiSettings.exampleLabel', { ns: 'admin' })}: {item.example}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PromptInstructionEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  badgeKey?: string;
  hintKey?: string;
  footerHintKey?: string;
}

const PromptInstructionEditor: React.FC<PromptInstructionEditorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  variant = 'primary',
  badgeKey = 'aiSettings.customInstructionsBadge',
  hintKey = 'aiSettings.customInstructionsHint',
  footerHintKey = 'aiSettings.editorFooterHint',
}) => {
  const { t } = useTranslation(['admin']);

  return (
    <div className={`${styles.promptEditorPanel} ${variant === 'secondary' ? styles.promptEditorPanelSecondary : ''}`}>
      <div className={styles.promptEditorPanelHeader}>
        <div className={styles.promptEditorPanelHeaderContent}>
          <span className={styles.promptEditorPanelBadge}>{t(badgeKey, { ns: 'admin' })}</span>
          <p className={styles.promptEditorPanelHint}>{t(hintKey, { ns: 'admin' })}</p>
        </div>
      </div>
      <div className={styles.promptEditorPanelBody}>
        <textarea
          className={styles.promptTextarea}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          spellCheck={false}
          rows={variant === 'secondary' ? 10 : 16}
        />
      </div>
      <div className={styles.promptEditorPanelFooter}>
        <span>{t(footerHintKey, { ns: 'admin' })}</span>
        <span>{t('aiSettings.characterCount', { ns: 'admin', count: value.length })}</span>
      </div>
    </div>
  );
};

const PromptEditorTab: React.FC<PromptEditorTabProps> = ({ useCase, canManage, onOverrideStateChange }) => {
  const { t } = useTranslation(['admin', 'common']);
  const { showToast } = useToast();
  const templateType = useCase.templateType;
  const isChannelOverride = isChannelSpecificExternalUnmutedUseCase(useCase.id);
  const usesUseCasePromptApi = usesDedicatedPromptOverride(useCase.id);

  // ── State ──
  const [config, setConfig] = useState<AiPromptConfig | null>(null);
  const [versions, setVersions] = useState<AiPromptVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewVersionNumber, setPreviewVersionNumber] = useState<number | null>(null);
  const [toolPolicyLoading, setToolPolicyLoading] = useState(true);
  const [toolPolicySaving, setToolPolicySaving] = useState(false);
  const [toolPolicyConfigured, setToolPolicyConfigured] = useState(false);
  const [toolFunctions, setToolFunctions] = useState<AiKernelFunctionSetting[]>([]);
  const [toolPluginCatalog, setToolPluginCatalog] = useState<AiKernelFunctionToolPlugin[]>([]);
  const [instructionDrafts, setInstructionDrafts] = useState<Record<string, string>>({});
  const [openInstructionFunctionId, setOpenInstructionFunctionId] = useState<string | null>(null);
  const [instructionSavingFunctionId, setInstructionSavingFunctionId] = useState<string | null>(null);
  const [historyInclusionSavingFunctionId, setHistoryInclusionSavingFunctionId] = useState<string | null>(null);
  const [autoPolicyLoading, setAutoPolicyLoading] = useState(true);
  const [autoPolicySaving, setAutoPolicySaving] = useState(false);
  const [autoPolicyConfigured, setAutoPolicyConfigured] = useState(false);
  const [prefetchLoading, setPrefetchLoading] = useState(true);
  const [prefetchSaving, setPrefetchSaving] = useState(false);
  const [prefetchConfigured, setPrefetchConfigured] = useState(false);

  // ── Draft form state ──
  const [draft, setDraft] = useState({
    systemPrompt: '',
    toneOfVoice: '',
    includeCompanyKnowledge: true,
    includeConversationHistory: true,
    maxTokensResponse: '' as string,
    resourceListInstruction: '',
    attachmentPolicy: createDefaultAttachmentPolicyDraft(),
    changeReason: '',
  });
  const [autoPolicyDraft, setAutoPolicyDraft] = useState({
    maxToolCalls: '3',
  });
  const [prefetchDraft, setPrefetchDraft] = useState({
    enabled: true,
  });
  const [openExampleSection, setOpenExampleSection] = useState<'knowledgeToolGuidance' | null>(null);
  const [activeSubSection, setActiveSubSection] = useState<UseCaseSubSection>('prompt');
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    setActiveSubSection('prompt');
    setShowVariables(false);
    setPreviewVersionNumber(null);
  }, [useCase.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setToolPolicyLoading(true);
    setAutoPolicyLoading(true);
    setPrefetchLoading(true);
    Promise.all([
      usesUseCasePromptApi ? getAiUseCasePromptConfig(useCase.id) : getAiPromptConfig(templateType),
      getAiKernelFunctionPolicy(useCase.id),
      getAiAutoFunctionInvocationPolicy(useCase.id),
      getAiResourceListPrefetchPreference(useCase.id),
    ])
      .then(([c, policy, autoPolicy, prefetchPreference]) => {
        if (cancelled) return;
        setConfig(c);
        setDraft({
          systemPrompt: c.systemPrompt ?? '',
          toneOfVoice: c.toneOfVoice ?? '',
          includeCompanyKnowledge: c.includeCompanyKnowledge,
          includeConversationHistory: c.includeConversationHistory,
          maxTokensResponse: c.maxTokensResponse != null ? String(c.maxTokensResponse) : '',
          resourceListInstruction: getResourceListInstruction(c.customFields),
          attachmentPolicy: parseAttachmentPolicyDraft(c.customFields),
          changeReason: '',
        });
        setToolPolicyConfigured(policy.isConfigured);
        setToolFunctions(policy.functions);
        setToolPluginCatalog(policy.toolPlugins);
        setInstructionDrafts(buildInstructionDrafts(policy.functions));
        setOpenInstructionFunctionId(null);
        setAutoPolicyConfigured(autoPolicy.isConfigured);
        setAutoPolicyDraft({
          maxToolCalls: String(autoPolicy.maxToolCalls || 1),
        });
        setPrefetchConfigured(prefetchPreference.isConfigured);
        setPrefetchDraft({
          enabled: prefetchPreference.enabled,
        });
      })
      .catch(() => {
        if (!cancelled) showToast(t('aiSettings.failedToLoadConfig', { ns: 'admin' }), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setToolPolicyLoading(false);
          setAutoPolicyLoading(false);
          setPrefetchLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [templateType, useCase.id, usesUseCasePromptApi, showToast, t]);

  const enabledToolCount = toolFunctions.filter((item) => item.enabled).length;
  const { pluginGroups: toolPluginGroups, ungroupedTools } = useMemo(
    () => groupToolFunctionsByPlugin(toolFunctions, toolPluginCatalog),
    [toolFunctions, toolPluginCatalog],
  );
  const supportsResourceInstruction = toolFunctions.some((item) => item.functionId === resourceToolFunctionId);
  const availableVariables = config?.availableVariables ?? [];

  const toggleToolFunction = (functionId: string) => {
    setToolFunctions((current) =>
      current.map((item) =>
        item.functionId === functionId
          ? { ...item, enabled: !item.enabled }
          : item));
  };

  const handleSaveToolPolicy = async () => {
    setToolPolicySaving(true);
    try {
      const allowedFunctionIds = toolFunctions
        .filter((item) => item.enabled)
        .map((item) => item.functionId);

      const updated = await updateAiKernelFunctionPolicy(useCase.id, allowedFunctionIds);
      setToolPolicyConfigured(updated.isConfigured);
      setToolFunctions(updated.functions);
      setToolPluginCatalog(updated.toolPlugins);
      onOverrideStateChange?.();
      showToast(t('aiSettings.kernelPolicySaved', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToSaveKernelPolicy', { ns: 'admin' }), 'error');
    } finally {
      setToolPolicySaving(false);
    }
  };

  const handleToggleInstructionEditor = (functionId: string) => {
    setOpenInstructionFunctionId((current) => (current === functionId ? null : functionId));
  };

  const handleInstructionChange = (functionId: string, value: string) => {
    setInstructionDrafts((current) => ({ ...current, [functionId]: value }));
  };

  const handleSaveInstruction = async (functionId: string) => {
    const trimmedInstruction = (instructionDrafts[functionId] ?? '').trim();
    setInstructionSavingFunctionId(functionId);
    try {
      const updated = await updateAiKernelPluginInstruction(
        functionId,
        trimmedInstruction.length > 0 ? trimmedInstruction : null,
      );
      setToolFunctions((current) =>
        current.map((item) =>
          item.functionId === functionId
            ? { ...item, instruction: updated.instruction }
            : item));
      setInstructionDrafts((current) => ({ ...current, [functionId]: updated.instruction ?? '' }));
      showToast(
        t(updated.instruction ? 'aiSettings.toolInstructionSaved' : 'aiSettings.toolInstructionCleared', { ns: 'admin' }),
        'success',
      );
    } catch {
      showToast(t('aiSettings.failedToSaveToolInstruction', { ns: 'admin' }), 'error');
    } finally {
      setInstructionSavingFunctionId(null);
    }
  };

  const handleToggleHistoryInclusion = async (functionId: string, includeResultsInHistory: boolean) => {
    setHistoryInclusionSavingFunctionId(functionId);
    try {
      const updated = await updateAiToolHistoryInclusion(functionId, includeResultsInHistory);
      setToolFunctions((current) =>
        current.map((item) =>
          item.functionId === functionId
            ? { ...item, includeResultsInHistory: updated.includeResultsInHistory }
            : item));
      showToast(
        t(updated.includeResultsInHistory
          ? 'aiSettings.toolHistoryInclusionEnabled'
          : 'aiSettings.toolHistoryInclusionDisabled', { ns: 'admin' }),
        'success',
      );
    } catch {
      showToast(t('aiSettings.failedToSaveToolHistoryInclusion', { ns: 'admin' }), 'error');
    } finally {
      setHistoryInclusionSavingFunctionId(null);
    }
  };

  const renderToolPolicyCard = (item: AiKernelFunctionSetting) => {
    const descriptionKey = getKernelFunctionDescriptionKey(item.functionId);
    const checkboxId = `tool-function-${useCase.id}-${item.functionId}`;
    const instructionOpen = openInstructionFunctionId === item.functionId;
    const instructionDraft = instructionDrafts[item.functionId] ?? '';
    const instructionSaving = instructionSavingFunctionId === item.functionId;
    const instructionDirty = instructionDraft.trim() !== (item.instruction ?? '');
    const historyCheckboxId = `tool-history-${useCase.id}-${item.functionId}`;
    const historyInclusionSaving = historyInclusionSavingFunctionId === item.functionId;
    const sharedPlugins = item.toolPlugins.slice(1);

    return (
      <div key={item.functionId} className={styles.toolPolicyCard}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={item.enabled}
          disabled={!canManage || toolPolicySaving || saving}
          onChange={() => toggleToolFunction(item.functionId)}
        />
        <div className={styles.toolPolicyMeta}>
          <label className={styles.toolPolicyName} htmlFor={checkboxId}>{item.functionDisplayName}</label>
          <span className={styles.toolPolicyId}>{item.functionId}</span>
          {sharedPlugins.length > 0 ? (
            <span className={styles.toolPluginChips}>
              {sharedPlugins.map((plugin) => (
                <span
                  key={plugin.pluginId}
                  className={styles.toolPluginChip}
                  title={t('aiSettings.toolPluginAlsoIn', {
                    ns: 'admin',
                    names: sharedPlugins.map((shared) => shared.displayName).join(', '),
                  })}
                >
                  {plugin.displayName}
                </span>
              ))}
            </span>
          ) : null}
          {descriptionKey ? (
            <span className={styles.toolPolicyDescription}>{t(descriptionKey, { ns: 'admin' })}</span>
          ) : null}
          {item.supportsInstruction ? (
            <div className={styles.toolInstruction}>
              <button
                type="button"
                className={styles.toolInstructionToggle}
                aria-expanded={instructionOpen}
                onClick={() => handleToggleInstructionEditor(item.functionId)}
              >
                {item.instruction
                  ? t('aiSettings.toolInstructionEdit', { ns: 'admin' })
                  : t('aiSettings.toolInstructionAdd', { ns: 'admin' })}
                {item.instruction ? <span className={styles.toolInstructionDot} aria-hidden="true" /> : null}
              </button>
              {instructionOpen ? (
                <div className={styles.toolInstructionEditor}>
                  <textarea
                    className={styles.toolInstructionTextarea}
                    value={instructionDraft}
                    rows={4}
                    maxLength={AI_KERNEL_PLUGIN_INSTRUCTION_MAX_LENGTH}
                    placeholder={t('aiSettings.toolInstructionPlaceholder', { ns: 'admin' })}
                    disabled={!canManage || instructionSaving}
                    onChange={(e) => handleInstructionChange(item.functionId, e.target.value)}
                  />
                  <span className={styles.fieldHelp}>{t('aiSettings.toolInstructionHelp', { ns: 'admin' })}</span>
                  <div className={styles.toolInstructionActions}>
                    <span className={styles.toolInstructionCount}>
                      {t('aiSettings.characterCount', { ns: 'admin', count: instructionDraft.length })}
                    </span>
                    {canManage ? (
                      <button
                        type="button"
                        className={styles.btnPolicySave}
                        disabled={instructionSaving || !instructionDirty}
                        onClick={() => handleSaveInstruction(item.functionId)}
                      >
                        {instructionSaving
                          ? t('saving', { ns: 'common' })
                          : t('aiSettings.toolInstructionSave', { ns: 'admin' })}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {item.supportsHistoryInclusion ? (
            <label className={styles.toolHistoryToggle} htmlFor={historyCheckboxId}>
              <input
                id={historyCheckboxId}
                type="checkbox"
                checked={item.includeResultsInHistory}
                disabled={!canManage || historyInclusionSaving}
                onChange={(e) => handleToggleHistoryInclusion(item.functionId, e.target.checked)}
              />
              <span>{t('aiSettings.toolHistoryInclusion', { ns: 'admin' })}</span>
            </label>
          ) : null}
        </div>
        <span className={item.accessMode === 'write' ? styles.accessWrite : styles.accessRead}>
          {item.accessMode === 'write' ? t('aiSettings.writeAccess', { ns: 'admin' }) : t('aiSettings.readAccess', { ns: 'admin' })}
        </span>
      </div>
    );
  };

  // A shared tool keeps its single checkbox on the owning plugin's card and is only referenced elsewhere.
  const renderToolPolicyReference = ({ tool, ownerDisplayName }: ToolPolicyPluginMember) => (
    <div key={tool.functionId} className={styles.toolPolicyReference}>
      <span />
      <div className={styles.toolPolicyMeta}>
        <span className={styles.toolPolicyReferenceName}>{tool.functionDisplayName}</span>
        <span className={styles.toolPolicyId}>{tool.functionId}</span>
        <span className={styles.toolPolicyReferenceNote}>
          {t('aiSettings.toolPluginManagedUnder', { ns: 'admin', pluginName: ownerDisplayName })}
        </span>
      </div>
      <span className={tool.accessMode === 'write' ? styles.accessWrite : styles.accessRead}>
        {tool.accessMode === 'write' ? t('aiSettings.writeAccess', { ns: 'admin' }) : t('aiSettings.readAccess', { ns: 'admin' })}
      </span>
    </div>
  );

  const handleSaveAutoPolicy = async () => {
    const parsedMaxToolCalls = Number.parseInt(autoPolicyDraft.maxToolCalls, 10);
    const maxToolCalls = Number.isFinite(parsedMaxToolCalls) && parsedMaxToolCalls > 0
      ? parsedMaxToolCalls
      : 1;

    setAutoPolicySaving(true);
    try {
      const updated = await updateAiAutoFunctionInvocationPolicy(useCase.id, maxToolCalls);
      setAutoPolicyConfigured(updated.isConfigured);
      setAutoPolicyDraft({
        maxToolCalls: String(updated.maxToolCalls),
      });
      onOverrideStateChange?.();
      showToast(t('aiSettings.autoPolicySaved', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToSaveAutoPolicy', { ns: 'admin' }), 'error');
    } finally {
      setAutoPolicySaving(false);
    }
  };

  const handleSavePrefetchPreference = async () => {
    setPrefetchSaving(true);
    try {
      const updated = await updateAiResourceListPrefetchPreference(useCase.id, prefetchDraft.enabled);
      setPrefetchConfigured(updated.isConfigured);
      setPrefetchDraft({ enabled: updated.enabled });
      onOverrideStateChange?.();
      showToast(t('aiSettings.resourceListPrefetchSaved', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToSaveResourceListPrefetch', { ns: 'admin' }), 'error');
    } finally {
      setPrefetchSaving(false);
    }
  };

  const handleResetPrefetchPreference = async () => {
    setPrefetchSaving(true);
    try {
      const updated = await clearAiResourceListPrefetchPreference(useCase.id);
      setPrefetchConfigured(updated.isConfigured);
      setPrefetchDraft({ enabled: updated.enabled });
      onOverrideStateChange?.();
      showToast(t('aiSettings.resourceListPrefetchReset', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToResetResourceListPrefetch', { ns: 'admin' }), 'error');
    } finally {
      setPrefetchSaving(false);
    }
  };

  // ── Version history ──
  const loadVersionHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const h = usesUseCasePromptApi
        ? await getAiUseCasePromptVersionHistory(useCase.id)
        : await getAiPromptVersionHistory(templateType);
      setVersions(h);
    } catch {
      showToast(t('aiSettings.failedToLoadVersionHistory', { ns: 'admin' }), 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, [templateType, useCase.id, usesUseCasePromptApi, showToast, t]);

  useEffect(() => {
    void loadVersionHistory();
  }, [loadVersionHistory]);

  // ── Save ──
  const handleSave = async () => {
    if (!draft.systemPrompt.trim()) {
      showToast(t('aiSettings.systemPromptRequired', { ns: 'admin' }), 'error');
      return;
    }
    setSaving(true);
    try {
      const customFields = buildCustomFieldsValue(
        config?.customFields,
        draft.resourceListInstruction,
        draft.attachmentPolicy,
      );

      const request: UpdateAiPromptConfigRequest = {
        systemPrompt: draft.systemPrompt.trim(),
        toneOfVoice: draft.toneOfVoice.trim() || null,
        includeCompanyKnowledge: draft.includeCompanyKnowledge,
        includeConversationHistory: draft.includeConversationHistory,
        maxTokensResponse: draft.maxTokensResponse ? Number(draft.maxTokensResponse) : null,
        customFields,
        changeReason: draft.changeReason.trim() || null,
      };
      const updated = usesUseCasePromptApi
        ? await updateAiUseCasePromptConfig(useCase.id, request)
        : await updateAiPromptConfig(templateType, request);
      setConfig(updated);
      onOverrideStateChange?.();
      const updatedPrefix = getUseCaseTranslationPrefix(useCase.id);
      setDraft((d) => ({
        ...d,
        systemPrompt: updated.systemPrompt ?? '',
        toneOfVoice: updated.toneOfVoice ?? '',
        includeCompanyKnowledge: updated.includeCompanyKnowledge,
        includeConversationHistory: updated.includeConversationHistory,
        maxTokensResponse: updated.maxTokensResponse != null ? String(updated.maxTokensResponse) : '',
        resourceListInstruction: getResourceListInstruction(updated.customFields),
        attachmentPolicy: parseAttachmentPolicyDraft(updated.customFields),
        changeReason: '',
      }));
      showToast(t('aiSettings.promptSavedVersion', { ns: 'admin', version: updated.version, context: updatedPrefix }), 'success');
    } catch {
      showToast(t('aiSettings.failedToSavePrompt', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Reset ──
  const handleReset = async () => {
    if (!window.confirm(t(isChannelOverride ? 'aiSettings.channelResetPromptConfirm' : 'aiSettings.resetConfirm', { ns: 'admin' }))) return;
    setSaving(true);
    try {
      const fresh = usesUseCasePromptApi
        ? await resetAiUseCasePromptConfig(useCase.id)
        : await (async () => {
          await resetAiPromptToDefault(templateType);
          return getAiPromptConfig(templateType);
        })();
      setConfig(fresh);
      onOverrideStateChange?.();
      setDraft({
        systemPrompt: fresh.systemPrompt ?? '',
        toneOfVoice: fresh.toneOfVoice ?? '',
        includeCompanyKnowledge: fresh.includeCompanyKnowledge,
        includeConversationHistory: fresh.includeConversationHistory,
        maxTokensResponse: fresh.maxTokensResponse != null ? String(fresh.maxTokensResponse) : '',
        resourceListInstruction: getResourceListInstruction(fresh.customFields),
        attachmentPolicy: parseAttachmentPolicyDraft(fresh.customFields),
        changeReason: '',
      });
      showToast(t('aiSettings.promptReset', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToResetPrompt', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Revert to version ──
  const handleRevert = async (version: number) => {
    if (usesUseCasePromptApi) {
      showToast(t('aiSettings.channelHistoryRestoreUnavailable', { ns: 'admin' }), 'error');
      return;
    }
    if (!window.confirm(t('aiSettings.restoreConfirm', { ns: 'admin', version }))) return;
    setSaving(true);
    try {
      const reverted = await revertAiPromptToVersion(templateType, version);
      setConfig(reverted);
      setDraft({
        systemPrompt: reverted.systemPrompt ?? '',
        toneOfVoice: reverted.toneOfVoice ?? '',
        includeCompanyKnowledge: reverted.includeCompanyKnowledge,
        includeConversationHistory: reverted.includeConversationHistory,
        maxTokensResponse: reverted.maxTokensResponse != null ? String(reverted.maxTokensResponse) : '',
        resourceListInstruction: getResourceListInstruction(reverted.customFields),
        attachmentPolicy: parseAttachmentPolicyDraft(reverted.customFields),
        changeReason: '',
      });
      setPreviewVersionNumber(null);
      // Refresh history
      const h = await getAiPromptVersionHistory(templateType);
      setVersions(h);
      showToast(t('aiSettings.restoredVersion', { ns: 'admin', version }), 'success');
    } catch {
      showToast(t('aiSettings.failedToRestorePrompt', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUseSharedChannelDefault = async () => {
    if (!isChannelOverride) {
      return;
    }
    if (!window.confirm(t('aiSettings.channelUseSharedDefaultConfirm', { ns: 'admin' }))) return;
    setSaving(true);
    try {
      await clearAiExternalChannelOverride(useCase.id);
      const [fresh, policy, autoPolicy, prefetchPreference] = await Promise.all([
        getAiUseCasePromptConfig(useCase.id),
        getAiKernelFunctionPolicy(useCase.id),
        getAiAutoFunctionInvocationPolicy(useCase.id),
        getAiResourceListPrefetchPreference(useCase.id),
      ]);
      setConfig(fresh);
      setDraft({
        systemPrompt: fresh.systemPrompt ?? '',
        toneOfVoice: fresh.toneOfVoice ?? '',
        includeCompanyKnowledge: fresh.includeCompanyKnowledge,
        includeConversationHistory: fresh.includeConversationHistory,
        maxTokensResponse: fresh.maxTokensResponse != null ? String(fresh.maxTokensResponse) : '',
        resourceListInstruction: getResourceListInstruction(fresh.customFields),
        attachmentPolicy: parseAttachmentPolicyDraft(fresh.customFields),
        changeReason: '',
      });
      setToolPolicyConfigured(policy.isConfigured);
      setToolFunctions(policy.functions);
      setToolPluginCatalog(policy.toolPlugins);
      setInstructionDrafts(buildInstructionDrafts(policy.functions));
      setAutoPolicyConfigured(autoPolicy.isConfigured);
      setAutoPolicyDraft({
        maxToolCalls: String(autoPolicy.maxToolCalls || 1),
      });
      setPrefetchConfigured(prefetchPreference.isConfigured);
      setPrefetchDraft({ enabled: prefetchPreference.enabled });
      onOverrideStateChange?.();
      showToast(t('aiSettings.channelUseSharedDefaultSuccess', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.channelUseSharedDefaultFailed', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Version preview ──
  const orderedVersions = useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions],
  );

  const previewVersion = previewVersionNumber != null
    ? versions.find((v) => v.version === previewVersionNumber) ?? null
    : null;

  const previewDraft = useMemo(() => (previewVersion
    ? {
      systemPrompt: previewVersion.systemPrompt,
      toneOfVoice: previewVersion.toneOfVoice ?? '',
      includeCompanyKnowledge: previewVersion.includeCompanyKnowledge,
      includeConversationHistory: previewVersion.includeConversationHistory,
      maxTokensResponse: previewVersion.maxTokensResponse != null ? String(previewVersion.maxTokensResponse) : '',
      resourceListInstruction: getResourceListInstruction(previewVersion.customFields),
      attachmentPolicy: parseAttachmentPolicyDraft(previewVersion.customFields),
      changeReason: '',
    }
    : null), [previewVersion]);

  // The live draft stays untouched while previewing, so exiting preview returns unsaved edits.
  const formValues = previewDraft ?? draft;
  const isPreviewing = previewDraft !== null;
  const fieldsDisabled = !canManage || saving || isPreviewing;

  const useCasePrefix = getUseCaseTranslationPrefix(useCase.id);
  const localizedUseCase = {
    ...useCase,
    tabLabel: t(`${useCasePrefix}.tabLabel`, { ns: 'admin', defaultValue: useCase.tabLabel }),
    title: t(`${useCasePrefix}.title`, { ns: 'admin', defaultValue: useCase.title }),
    description: t(`${useCasePrefix}.description`, { ns: 'admin', defaultValue: useCase.description }),
    templateScopeNote: useCase.templateScopeNote
      ? t(`${useCasePrefix}.scopeNote`, { ns: 'admin', defaultValue: useCase.templateScopeNote })
      : undefined,
  };

  if (loading) {
    return (
      <div className={styles.useCaseLoading}>
        <Spinner size={36} text={t('aiSettings.loadingConfiguration', { ns: 'admin' })} />
      </div>
    );
  }

  const getSubSectionLabel = (section: UseCaseSubSection): string => {
    switch (section) {
      case 'prompt':
        return t('aiSettings.useCaseSections.prompt', { ns: 'admin' });
      case 'tools':
        return t('aiSettings.useCaseSections.tools', { ns: 'admin' });
      case 'advanced':
        return t('aiSettings.useCaseSections.advanced', { ns: 'admin' });
      default:
        return section;
    }
  };

  const getSubSectionDescription = (section: UseCaseSubSection): string => {
    switch (section) {
      case 'prompt':
        return t('aiSettings.useCaseSections.promptDescription', { ns: 'admin' });
      case 'tools':
        return t('aiSettings.useCaseSections.toolsDescription', { ns: 'admin' });
      case 'advanced':
        return t('aiSettings.useCaseSections.advancedDescription', { ns: 'admin' });
      default:
        return '';
    }
  };

  return (
    <div className={styles.useCaseLayout}>
      <header className={styles.useCaseHero}>
        <div className={styles.useCaseHeroBody}>
          <div className={styles.useCaseHeroMain}>
            <span className={styles.useCaseHeroEyebrow}>{localizedUseCase.tabLabel}</span>
            <h2 className={styles.useCaseHeroTitle}>{localizedUseCase.title}</h2>
            <p className={styles.useCaseHeroDescription}>{localizedUseCase.description}</p>
            {localizedUseCase.templateScopeNote ? (
              <p className={styles.useCaseHeroNote}>{localizedUseCase.templateScopeNote}</p>
            ) : null}
          </div>
          <div className={styles.useCaseHeroAside}>
            <div className={styles.useCaseStatGrid}>
              <div className={styles.useCaseStatCard}>
                <span className={styles.useCaseStatLabel}>{t('aiSettings.useCaseSections.toolsEnabled', { ns: 'admin' })}</span>
                <span className={styles.useCaseStatValue}>{enabledToolCount}</span>
              </div>
              <div className={styles.useCaseStatCard}>
                <span className={styles.useCaseStatLabel}>{t('aiSettings.useCaseSections.promptVersion', { ns: 'admin' })}</span>
                <span className={styles.useCaseStatValue}>
                  {config?.isDefault
                    ? t('aiSettings.defaultValue', { ns: 'admin' })
                    : `v${config?.version ?? 0}`}
                </span>
              </div>
            </div>
            <div className={styles.useCaseHeroBadges}>
              <div className={styles.useCaseBadges}>
                {config?.isDefault ? (
                  <span className={styles.defaultBadge}>{t('aiSettings.usingSystemDefault', { ns: 'admin' })}</span>
                ) : (
                  <span className={styles.policyConfigured}>{t('aiSettings.tenantOverride', { ns: 'admin' })}</span>
                )}
                <span className={enabledToolCount > 0 ? styles.policyBadgeAllowed : styles.policyBadgeDisabled}>
                  {enabledToolCount > 0
                    ? t('aiSettings.functionsEnabledCount', { ns: 'admin', count: enabledToolCount })
                    : t('aiSettings.noFunctionsEnabled', { ns: 'admin' })}
                </span>
                {toolPolicyConfigured ? (
                  <span className={styles.policyConfigured}>{t('aiSettings.useCaseSections.customTools', { ns: 'admin' })}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      {isChannelOverride ? (
        <div className={`${styles.channelBanner} ${config?.isInherited === false || toolPolicyConfigured || autoPolicyConfigured || prefetchConfigured ? styles.channelBannerCustom : styles.channelBannerInherited}`}>
          <div>
            <strong>
              {config?.isInherited === false || toolPolicyConfigured || autoPolicyConfigured || prefetchConfigured
                ? t('aiSettings.channelCustomizedTitle', { ns: 'admin' })
                : t('aiSettings.channelInheritedTitle', { ns: 'admin' })}
            </strong>
            <p>
              {config?.isInherited === false || toolPolicyConfigured || autoPolicyConfigured || prefetchConfigured
                ? t('aiSettings.channelCustomizedHelp', { ns: 'admin' })
                : t('aiSettings.channelInheritedHelp', { ns: 'admin' })}
            </p>
          </div>
          {canManage && (config?.isInherited === false || toolPolicyConfigured || autoPolicyConfigured || prefetchConfigured) ? (
            <button
              type="button"
              className={styles.btnReset}
              disabled={saving}
              onClick={() => void handleUseSharedChannelDefault()}
            >
              {t('aiSettings.channelUseSharedDefault', { ns: 'admin' })}
            </button>
          ) : null}
        </div>
      ) : null}

      <nav className={styles.useCaseSubNav} aria-label={t('aiSettings.useCaseSections.navigation', { ns: 'admin' })}>
        {USE_CASE_SUB_SECTIONS.map((section) => (
          <button
            key={section}
            type="button"
            className={`${styles.useCaseSubNavButton} ${activeSubSection === section ? styles.useCaseSubNavButtonActive : ''}`}
            aria-current={activeSubSection === section ? 'page' : undefined}
            onClick={() => setActiveSubSection(section)}
          >
            <span className={styles.useCaseSubNavLabel}>{getSubSectionLabel(section)}</span>
            <span className={styles.useCaseSubNavHint}>{getSubSectionDescription(section)}</span>
          </button>
        ))}
      </nav>

      {previewVersion ? (
        <div className={styles.previewBanner} role="status">
          <div className={styles.previewBannerText}>
            <strong>{t('aiSettings.versionPreviewTitle', { ns: 'admin', version: previewVersion.version })}</strong>
            <p>
              {t('aiSettings.versionPreviewMeta', {
                ns: 'admin',
                date: formatDateTime(previewVersion.changedAt),
                currentVersion: config?.version ?? 0,
              })}
            </p>
            {previewVersion.changeReason ? (
              <p className={styles.previewBannerReason}>{previewVersion.changeReason}</p>
            ) : null}
          </div>
          <div className={styles.previewBannerActions}>
            {canManage ? (
              <button
                type="button"
                className={styles.btnPreviewRestore}
                disabled={saving}
                onClick={() => void handleRevert(previewVersion.version)}
              >
                {t('aiSettings.versionPreviewRestore', { ns: 'admin' })}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.btnPreviewExit}
              disabled={saving}
              onClick={() => setPreviewVersionNumber(null)}
            >
              {t('aiSettings.versionPreviewExit', { ns: 'admin' })}
            </button>
          </div>
        </div>
      ) : null}

      <div className={styles.useCaseContent}>
        {activeSubSection === 'tools' ? (
          <div className={styles.useCaseSectionStack}>
            <section className={styles.useCaseCard}>
              <SectionHeader
                title={t('aiSettings.kernelFunctionAccess', { ns: 'admin' })}
                description={[
                  t('aiSettings.kernelFunctionAccessHelp', { ns: 'admin' }),
                  t('aiSettings.toolInstructionScopeNote', { ns: 'admin' }),
                  t('aiSettings.toolHistoryInclusionScopeNote', { ns: 'admin' }),
                  isCustomerFacingReplyUseCase(useCase.id)
                    ? t('aiSettings.kernelFunctionAccessHelpCustomerFacing', { ns: 'admin' })
                    : null,
                ].filter(Boolean).join(' ')}
              />
              {toolPolicyLoading ? (
                <div className={styles.toolPolicyLoading}>{t('aiSettings.loadingFunctionPermissions', { ns: 'admin' })}</div>
              ) : toolFunctions.length === 0 && toolPluginGroups.length === 0 ? (
                <div className={styles.toolPolicyEmpty}>{t('aiSettings.noKernelFunctions', { ns: 'admin' })}</div>
              ) : (
                <>
                  {toolPluginGroups.length === 0 ? (
                    <div className={styles.toolPolicyGrid}>
                      {toolFunctions.map(renderToolPolicyCard)}
                    </div>
                  ) : (
                    <div className={styles.toolPolicySections}>
                      {toolPluginGroups.map((group) => (
                        <div key={group.pluginId} className={styles.toolPolicySection}>
                          <div className={styles.toolPolicySectionHeader}>
                            <span className={styles.toolPolicySectionTitle}>{group.displayName}</span>
                            {group.isPublished ? null : (
                              <span className={styles.toolPluginDraftBadge}>
                                {t('aiSettings.toolPluginDraftBadge', { ns: 'admin' })}
                              </span>
                            )}
                            <span className={styles.toolPolicySectionCount}>
                              {t('aiSettings.toolPluginGroupCount', {
                                ns: 'admin',
                                enabled: group.members.filter((member) => member.tool.enabled).length,
                                count: group.members.length,
                              })}
                            </span>
                          </div>
                          <span className={styles.toolPolicySectionHint}>
                            {t(
                              group.isPublished
                                ? 'aiSettings.toolPluginGroupHint'
                                : 'aiSettings.toolPluginGroupDraftHint',
                              { ns: 'admin', pluginName: group.kernelPluginName },
                            )}
                          </span>
                          {group.members.length === 0 ? (
                            <div className={styles.toolPolicyEmpty}>
                              {t('aiSettings.toolPluginGroupEmpty', { ns: 'admin' })}
                            </div>
                          ) : (
                            <div className={styles.toolPolicyGrid}>
                              {group.members.map((member) => (member.isOwned
                                ? renderToolPolicyCard(member.tool)
                                : renderToolPolicyReference(member)))}
                            </div>
                          )}
                        </div>
                      ))}
                      {ungroupedTools.length > 0 ? (
                        <div className={styles.toolPolicySection}>
                          <div className={styles.toolPolicySectionHeader}>
                            <span className={styles.toolPolicySectionTitle}>
                              {t('aiSettings.toolPluginUngroupedTitle', { ns: 'admin' })}
                            </span>
                            <span className={styles.toolPolicySectionCount}>
                              {t('aiSettings.toolPluginGroupCount', {
                                ns: 'admin',
                                enabled: ungroupedTools.filter((item) => item.enabled).length,
                                count: ungroupedTools.length,
                              })}
                            </span>
                          </div>
                          <div className={styles.toolPolicyGrid}>
                            {ungroupedTools.map(renderToolPolicyCard)}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {canManage ? (
                    <div className={styles.toolPolicyActions}>
                      <button
                        className={styles.btnPolicySave}
                        onClick={handleSaveToolPolicy}
                        disabled={toolPolicySaving || saving}
                      >
                        {toolPolicySaving ? t('saving', { ns: 'common' }) : t('aiSettings.applyFunctionAccess', { ns: 'admin' })}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </section>

            <section className={styles.useCaseCard}>
              <SectionHeader title={t('aiSettings.autoFunctionInvocation', { ns: 'admin' })} />
              {autoPolicyLoading ? (
                <div className={styles.toolPolicyLoading}>{t('aiSettings.loadingAutoPolicy', { ns: 'admin' })}</div>
              ) : (
                <div className={styles.autoPolicyCard}>
                  <div className={styles.useCaseBadges}>
                    <span className={styles.policyBadgeAllowed}>
                      {t('aiSettings.maxToolCallsValue', { ns: 'admin', count: Number.parseInt(autoPolicyDraft.maxToolCalls || '1', 10) || 1 })}
                    </span>
                    {autoPolicyConfigured ? <span className={styles.policyConfigured}>{t('aiSettings.tenantOverride', { ns: 'admin' })}</span> : null}
                  </div>
                  <div className={styles.autoPolicyGrid}>
                    <div className={styles.controlGroup}>
                      <label className={styles.controlLabel} htmlFor={`auto-max-tool-calls-${useCase.id}`}>{t('aiSettings.maxToolCalls', { ns: 'admin' })}</label>
                      <input
                        id={`auto-max-tool-calls-${useCase.id}`}
                        type="number"
                        className={styles.numberInput}
                        value={autoPolicyDraft.maxToolCalls}
                        min={1}
                        max={20}
                        disabled={!canManage || autoPolicySaving || saving}
                        onChange={(e) => setAutoPolicyDraft((current) => ({ ...current, maxToolCalls: e.target.value }))}
                      />
                      <span className={styles.fieldHelp}>{t('aiSettings.maxToolCallsHelp', { ns: 'admin' })}</span>
                    </div>
                  </div>
                  {canManage ? (
                    <div className={styles.toolPolicyActions}>
                      <button
                        className={styles.btnPolicySave}
                        onClick={handleSaveAutoPolicy}
                        disabled={autoPolicySaving || saving}
                      >
                        {autoPolicySaving ? t('saving', { ns: 'common' }) : t('aiSettings.applyAutoPolicy', { ns: 'admin' })}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </section>

            <section className={styles.useCaseCard}>
              <SectionHeader
                title={t('aiSettings.resourceListPrefetch', { ns: 'admin' })}
                description={t('aiSettings.resourceListPrefetchDescription', { ns: 'admin' })}
              />
              {prefetchLoading ? (
                <div className={styles.toolPolicyLoading}>{t('aiSettings.loadingResourceListPrefetch', { ns: 'admin' })}</div>
              ) : (
                <div className={styles.autoPolicyCard}>
                  <div className={styles.useCaseBadges}>
                    <span className={prefetchDraft.enabled ? styles.policyBadgeAllowed : styles.policyBadgeDisabled}>
                      {prefetchDraft.enabled
                        ? t('aiSettings.resourceListPrefetchEnabledBadge', { ns: 'admin' })
                        : t('aiSettings.resourceListPrefetchDisabledBadge', { ns: 'admin' })}
                    </span>
                    {prefetchConfigured ? <span className={styles.policyConfigured}>{t('aiSettings.tenantOverride', { ns: 'admin' })}</span> : null}
                  </div>
                  <div className={styles.toggleRow}>
                    <div className={styles.toggleInfo}>
                      <span className={styles.toggleTitle}>{t('aiSettings.resourceListPrefetchEnabled', { ns: 'admin' })}</span>
                      <span className={styles.toggleHelp}>{t('aiSettings.resourceListPrefetchHelp', { ns: 'admin' })}</span>
                    </div>
                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={prefetchDraft.enabled}
                        disabled={!canManage || prefetchSaving || saving}
                        onChange={(e) => setPrefetchDraft({ enabled: e.target.checked })}
                      />
                      <span className={styles.toggleSlider} />
                    </label>
                  </div>
                  {canManage ? (
                    <div className={styles.toolPolicyActions}>
                      <button
                        className={styles.btnPolicySave}
                        onClick={handleSavePrefetchPreference}
                        disabled={prefetchSaving || saving}
                      >
                        {prefetchSaving ? t('saving', { ns: 'common' }) : t('aiSettings.applyResourceListPrefetch', { ns: 'admin' })}
                      </button>
                      {prefetchConfigured ? (
                        <button
                          className={styles.btnReset}
                          onClick={handleResetPrefetchPreference}
                          disabled={prefetchSaving || saving}
                        >
                          {t('aiSettings.resetResourceListPrefetch', { ns: 'admin' })}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          </div>
        ) : null}

        {activeSubSection === 'advanced' ? (
          <div className={styles.useCaseSectionStack}>
            {supportsResourceInstruction ? (
              <section className={styles.useCaseCard}>
                <SectionHeader
                  title={t('aiSettings.knowledgeToolGuidance', { ns: 'admin' })}
                  description={t('aiSettings.knowledgeToolGuidanceHelp', { ns: 'admin' })}
                  exampleJson={knowledgeToolGuidanceExample}
                  exampleLabel={t('aiSettings.exampleLabel', { ns: 'admin' })}
                  isExampleOpen={openExampleSection === 'knowledgeToolGuidance'}
                  onToggleExample={() => setOpenExampleSection((current) => current === 'knowledgeToolGuidance' ? null : 'knowledgeToolGuidance')}
                  exampleButtonId={`knowledge-guidance-example-button-${useCase.id}`}
                  examplePanelId={`knowledge-guidance-example-panel-${useCase.id}`}
                />
                <div className={styles.changeReasonWrapper}>
                  <PromptInstructionEditor
                    value={formValues.resourceListInstruction}
                    onChange={(value) => setDraft((current) => ({ ...current, resourceListInstruction: value }))}
                    placeholder={t('aiSettings.knowledgeToolGuidancePlaceholder', { ns: 'admin' })}
                    disabled={fieldsDisabled}
                    variant="secondary"
                    badgeKey="aiSettings.guidanceInstructionsBadge"
                    hintKey="aiSettings.guidanceInstructionsHint"
                    footerHintKey="aiSettings.guidanceInstructionsFooter"
                  />
                </div>
              </section>
            ) : null}

            <section className={styles.useCaseCard}>
              <SectionHeader
                title={t('aiSettings.attachmentPolicy', { ns: 'admin' })}
                description={t('aiSettings.attachmentPolicyHelp', { ns: 'admin' })}
              />
              <div className={styles.changeReasonWrapper}>
                <AiAttachmentPolicyEditor
                  value={formValues.attachmentPolicy}
                  onChange={(attachmentPolicy: AttachmentPolicyDraft) =>
                    setDraft((current) => ({ ...current, attachmentPolicy }))}
                  disabled={fieldsDisabled}
                />
              </div>
            </section>
          </div>
        ) : null}

        {activeSubSection === 'prompt' ? (
          <div className={styles.useCaseSectionStack}>
            <section className={styles.useCaseCard}>
              <SectionHeader
                title={t('aiSettings.systemPrompt', { ns: 'admin' })}
                actions={loadingHistory || orderedVersions.length > 0 ? (
                  <div className={styles.versionSwitcher}>
                    <label className={styles.versionSwitcherLabel} htmlFor={`prompt-version-${useCase.id}`}>
                      {t('aiSettings.versionHistory', { ns: 'admin' })}
                    </label>
                    <select
                      id={`prompt-version-${useCase.id}`}
                      className={styles.versionSwitcherSelect}
                      value={previewVersionNumber != null ? String(previewVersionNumber) : ''}
                      onChange={(e) => setPreviewVersionNumber(e.target.value ? Number(e.target.value) : null)}
                      disabled={saving || loadingHistory}
                    >
                      <option value="">
                        {t('aiSettings.versionCurrentOption', { ns: 'admin', version: config?.version ?? 0 })}
                      </option>
                      {orderedVersions.map((v) => (
                        <option key={v.version} value={v.version}>
                          {t('aiSettings.versionOption', {
                            ns: 'admin',
                            version: v.version,
                            date: formatDateTime(v.changedAt),
                          })}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              />
              {availableVariables.length > 0 ? (
                <div className={styles.variableDisclosure}>
                  <button
                    type="button"
                    className={styles.variableDisclosureButton}
                    aria-expanded={showVariables}
                    onClick={() => setShowVariables((current) => !current)}
                  >
                    {showVariables
                      ? t('aiSettings.useCaseSections.hideVariables', { ns: 'admin' })
                      : t('aiSettings.useCaseSections.showVariables', { ns: 'admin', count: availableVariables.length })}
                  </button>
                  {showVariables ? <PromptVariablesPanel variables={availableVariables} /> : null}
                </div>
              ) : null}
              <PromptInstructionEditor
                value={formValues.systemPrompt}
                onChange={(value) => setDraft((current) => ({ ...current, systemPrompt: value }))}
                placeholder={t('aiSettings.systemPromptPlaceholder', { ns: 'admin' })}
                disabled={fieldsDisabled}
              />
            </section>

            <section className={styles.useCaseCard}>
              <SectionHeader title={t('aiSettings.toneAndBehavior', { ns: 'admin' })} />
              <div className={styles.controlsRow}>
                <div className={styles.controlGroup}>
                  <label className={styles.controlLabel} htmlFor={`tone-${templateType}`}>{t('aiSettings.toneOfVoice', { ns: 'admin' })}</label>
                  <select
                    id={`tone-${templateType}`}
                    className={styles.select}
                    value={formValues.toneOfVoice}
                    onChange={(e) => setDraft((d) => ({ ...d, toneOfVoice: e.target.value }))}
                    disabled={fieldsDisabled}
                  >
                    <option value="">{t('aiSettings.defaultTone', { ns: 'admin' })}</option>
                    {TONE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{t(getToneTranslationKey(o.value), { ns: 'admin', defaultValue: o.label })}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.controlGroup}>
                  <label className={styles.controlLabel} htmlFor={`maxTokens-${templateType}`}>{t('aiSettings.maxTokensOptional', { ns: 'admin' })}</label>
                  <input
                    id={`maxTokens-${templateType}`}
                    type="number"
                    className={styles.numberInput}
                    value={formValues.maxTokensResponse}
                    onChange={(e) => setDraft((d) => ({ ...d, maxTokensResponse: e.target.value }))}
                    placeholder={t('aiSettings.defaultValue', { ns: 'admin' })}
                    min={50}
                    max={4096}
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>
            </section>

            <section className={styles.useCaseCard}>
              <SectionHeader title={t('aiSettings.context', { ns: 'admin' })} />
              <div className={styles.toggleRow}>
                <div className={styles.toggleInfo}>
                  <span className={styles.toggleTitle}>{t('aiSettings.includeCompanyKnowledge', { ns: 'admin' })}</span>
                  <span className={styles.toggleHelp}>{t('aiSettings.includeCompanyKnowledgeHelp', { ns: 'admin' })}</span>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={formValues.includeCompanyKnowledge}
                    onChange={(e) => setDraft((d) => ({ ...d, includeCompanyKnowledge: e.target.checked }))}
                    disabled={fieldsDisabled}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              <div className={styles.toggleRow}>
                <div className={styles.toggleInfo}>
                  <span className={styles.toggleTitle}>{t('aiSettings.includeConversationHistory', { ns: 'admin' })}</span>
                  <span className={styles.toggleHelp}>{t('aiSettings.includeConversationHistoryHelp', { ns: 'admin' })}</span>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={formValues.includeConversationHistory}
                    onChange={(e) => setDraft((d) => ({ ...d, includeConversationHistory: e.target.checked }))}
                    disabled={fieldsDisabled}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </section>

            {canManage && !isPreviewing ? (
              <section className={styles.useCaseCard}>
                <SectionHeader title={t('aiSettings.changeReasonOptional', { ns: 'admin' })} />
                <div className={styles.changeReasonWrapper}>
                  <input
                    type="text"
                    className={styles.changeReasonInput}
                    value={draft.changeReason}
                    onChange={(e) => setDraft((d) => ({ ...d, changeReason: e.target.value }))}
                    placeholder={t('aiSettings.changeReasonPlaceholder', { ns: 'admin' })}
                    maxLength={500}
                    disabled={saving}
                  />
                  <span className={styles.fieldHelp}>{t('aiSettings.changeReasonHelp', { ns: 'admin' })}</span>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>

      {canManage ? (
        <div className={styles.useCaseStickyFooterSlot}>
          {(activeSubSection === 'prompt' || activeSubSection === 'advanced') ? (
            <div className={styles.useCaseStickyFooter}>
              <div className={styles.useCaseStickyFooterInner}>
                <div className={styles.useCaseStickyFooterMeta}>
                  {!config?.isDefault ? (
                    <span className={styles.versionInfo}>{t('aiSettings.versionLabel', { ns: 'admin', version: config?.version ?? 0 })}</span>
                  ) : null}
                </div>
                <div className={styles.useCaseStickyFooterActions}>
                  <button
                    className={styles.btnSave}
                    onClick={handleSave}
                    disabled={saving || isPreviewing || !draft.systemPrompt.trim()}
                  >
                    {saving ? t('saving', { ns: 'common' }) : t('aiSettings.saveChanges', { ns: 'admin' })}
                  </button>
                  {!config?.isDefault ? (
                    <button
                      className={styles.btnReset}
                      onClick={handleReset}
                      disabled={saving || isPreviewing}
                    >
                      {t('aiSettings.resetToDefault', { ns: 'admin' })}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ProviderSettingsIcon: React.FC = () => (
  <svg className={styles.providerSettingsIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface AiGeneralSettingsTabProps {
  canManage: boolean;
}

const AiGeneralSettingsTab: React.FC<AiGeneralSettingsTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['admin', 'common']);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [activeChatProvider, setActiveChatProvider] = useState('');
  const [activeChatModelId, setActiveChatModelId] = useState('');
  const [providers, setProviders] = useState<AiProviderSetting[]>([]);
  const [draft, setDraft] = useState<UpdateAiProviderSettingsRequest>({
    chatProvider: 'Auto',
    azureOpenAiEnabled: true,
    openAiEnabled: true,
    deepSeekEnabled: true,
    anthropicEnabled: true,
    googleGeminiEnabled: true,
    mistralAiEnabled: true,
  });
  const [configuringProvider, setConfiguringProvider] = useState<AiProviderId | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getAiProviderSettings();
      setIsConfigured(settings.isConfigured);
      setActiveChatProvider(settings.activeChatProvider);
      setActiveChatModelId(settings.activeChatModelId);
      setProviders(settings.providers);
      setDraft(buildProviderDraft(settings));
    } catch {
      showToast(t('aiSettings.failedToLoadProviderSettings', { ns: 'admin' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const updateProviderEnabled = (providerId: 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI', enabled: boolean) => {
    setDraft((current) => {
      const next = {
        ...current,
        azureOpenAiEnabled: providerId === 'AzureOpenAI' ? enabled : current.azureOpenAiEnabled,
        openAiEnabled: providerId === 'OpenAI' ? enabled : current.openAiEnabled,
        deepSeekEnabled: providerId === 'DeepSeek' ? enabled : current.deepSeekEnabled,
        anthropicEnabled: providerId === 'Anthropic' ? enabled : current.anthropicEnabled,
        googleGeminiEnabled: providerId === 'GoogleGemini' ? enabled : current.googleGeminiEnabled,
        mistralAiEnabled: providerId === 'MistralAI' ? enabled : current.mistralAiEnabled,
      };

      if (!enabled && current.chatProvider === providerId) {
        next.chatProvider = 'Auto';
      }

      return next;
    });
  };

  const getDraftEnabled = (providerId: 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI'): boolean => {
    switch (providerId) {
      case 'AzureOpenAI':
        return draft.azureOpenAiEnabled;
      case 'OpenAI':
        return draft.openAiEnabled;
      case 'DeepSeek':
        return draft.deepSeekEnabled;
      case 'Anthropic':
        return draft.anthropicEnabled;
      case 'GoogleGemini':
        return draft.googleGeminiEnabled;
      case 'MistralAI':
        return draft.mistralAiEnabled;
      default:
        return false;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateAiProviderSettings(draft);
      setIsConfigured(updated.isConfigured);
      setActiveChatProvider(updated.activeChatProvider);
      setActiveChatModelId(updated.activeChatModelId);
      setProviders(updated.providers);
      setDraft(buildProviderDraft(updated));
      showToast(t('aiSettings.providerSettingsSaved', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToSaveProviderSettings', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const updated = await clearAiProviderSettings();
      setIsConfigured(updated.isConfigured);
      setActiveChatProvider(updated.activeChatProvider);
      setActiveChatModelId(updated.activeChatModelId);
      setProviders(updated.providers);
      setDraft(buildProviderDraft(updated));
      showToast(t('aiSettings.providerSettingsReset', { ns: 'admin' }), 'success');
    } catch {
      showToast(t('aiSettings.failedToResetProviderSettings', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={styles.toolPolicyLoading}>{t('aiSettings.loadingProviderSettings', { ns: 'admin' })}</div>;
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.useCaseHeader}>
        <h2 className={styles.useCaseHeaderTitle}>{t('aiSettings.general.title', { ns: 'admin' })}</h2>
        <p className={styles.useCaseDescription}>{t('aiSettings.general.description', { ns: 'admin' })}</p>
      </div>

      <div className={styles.section}>
        <SectionHeader
          title={t('aiSettings.providers.title', { ns: 'admin' })}
          description={t('aiSettings.providers.description', { ns: 'admin' })}
        />
        <div className={styles.autoPolicyCard}>
          <div className={styles.useCaseBadges}>
            <span className={styles.policyBadgeAllowed}>
              {t('aiSettings.providers.activeProvider', {
                ns: 'admin',
                provider: activeChatProvider,
                model: activeChatModelId,
              })}
            </span>
            {isConfigured && <span className={styles.policyConfigured}>{t('aiSettings.tenantOverride', { ns: 'admin' })}</span>}
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="chat-provider-select">
              {t('aiSettings.providers.chatProvider', { ns: 'admin' })}
            </label>
            <select
              id="chat-provider-select"
              className={styles.select}
              value={draft.chatProvider}
              disabled={!canManage || saving}
              onChange={(event) => setDraft((current) => ({
                ...current,
                chatProvider: event.target.value as AiChatProviderOption,
              }))}
            >
              {AI_CHAT_PROVIDER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(getChatProviderTranslationKey(option), { ns: 'admin' })}
                </option>
              ))}
            </select>
            <span className={styles.fieldHelp}>{t('aiSettings.providers.chatProviderHelp', { ns: 'admin' })}</span>
          </div>

          {providers.map((provider) => (
            <div key={provider.id} className={styles.toggleRow}>
              <div className={styles.toggleInfo}>
                <span className={styles.toggleTitle}>
                  {t(`${getProviderTranslationKey(provider.id)}.title`, {
                    ns: 'admin',
                    defaultValue: provider.displayName,
                  })}
                  {provider.isTenantConfigured ? (
                    <span className={styles.providerTenantBadge}>
                      {t('aiSettings.tenantOverride', { ns: 'admin' })}
                    </span>
                  ) : null}
                </span>
                <span className={styles.toggleHelp}>
                  {provider.isConfigured
                    ? t(`${getProviderTranslationKey(provider.id)}.help`, {
                      ns: 'admin',
                      model: provider.model ?? t('aiSettings.providers.unknownModel', { ns: 'admin' }),
                      endpoint: provider.endpoint ?? '',
                    })
                    : t('aiSettings.providers.notConfigured', { ns: 'admin' })}
                </span>
              </div>
              <div className={styles.providerRowActions}>
                <button
                  type="button"
                  className={styles.providerSettingsButton}
                  aria-label={t('aiSettings.providerConfiguration.openSettings', {
                    ns: 'admin',
                    provider: t(`${getProviderTranslationKey(provider.id)}.title`, {
                      ns: 'admin',
                      defaultValue: provider.displayName,
                    }),
                  })}
                  title={t('aiSettings.providerConfiguration.openSettings', {
                    ns: 'admin',
                    provider: t(`${getProviderTranslationKey(provider.id)}.title`, {
                      ns: 'admin',
                      defaultValue: provider.displayName,
                    }),
                  })}
                  onClick={() => setConfiguringProvider(provider.id as AiProviderId)}
                >
                  <ProviderSettingsIcon />
                </button>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={getDraftEnabled(provider.id as 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI')}
                    disabled={!canManage || saving || !provider.isConfigured}
                    onChange={(event) => updateProviderEnabled(
                      provider.id as 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI',
                      event.target.checked,
                    )}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>
          ))}

          {configuringProvider ? (
            <AiProviderConfigurationPanel
              providerId={configuringProvider}
              canManage={canManage}
              onClose={() => setConfiguringProvider(null)}
              onSaved={() => {
                void loadSettings();
              }}
            />
          ) : null}

          {canManage && (
            <div className={styles.toolPolicyActions}>
              <button
                className={styles.btnPolicySave}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t('saving', { ns: 'common' }) : t('aiSettings.applyProviderSettings', { ns: 'admin' })}
              </button>
              {isConfigured && (
                <button
                  className={styles.btnReset}
                  onClick={handleReset}
                  disabled={saving}
                >
                  {t('aiSettings.resetProviderSettings', { ns: 'admin' })}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getExternalChannelRailLabelKey = (item: ExternalChannelRailItem): string => {
  if (item.id === 'instagram-comments') {
    return 'aiSettings.externalChannels.instagramComments';
  }
  if (item.id === 'messenger-comments') {
    return 'aiSettings.externalChannels.messengerComments';
  }

  return `aiSettings.externalChannels.${item.id}`;
};

const getExternalChannelRailHintKey = (item: ExternalChannelRailItem): string | null => {
  switch (item.id) {
    case 'all':
      return 'aiSettings.externalChannels.allHint';
    case 'instagram':
      return 'aiSettings.externalChannels.instagramHint';
    case 'instagram-comments':
      return 'aiSettings.externalChannels.instagramCommentsHint';
    case 'messenger':
      return 'aiSettings.externalChannels.messengerHint';
    case 'messenger-comments':
      return 'aiSettings.externalChannels.messengerCommentsHint';
    default:
      return null;
  }
};

const COMMENT_RAIL_USE_CASES: Partial<Record<ExternalChannelRailItemId, AiPromptUseCaseTabConfig>> = {
  'instagram-comments': AI_INSTAGRAM_COMMENT_USE_CASE,
  'messenger-comments': AI_MESSENGER_COMMENT_USE_CASE,
};

const resolveExternalChannelRailUseCase = (
  parentUseCase: AiPromptUseCaseTabConfig,
  item: ExternalChannelRailItem,
): AiPromptUseCaseTabConfig => {
  const commentUseCase = COMMENT_RAIL_USE_CASES[item.id];
  if (commentUseCase) {
    return commentUseCase;
  }

  return {
    ...parentUseCase,
    id: item.useCaseId,
    templateScopeNote: item.id === 'all' ? parentUseCase.templateScopeNote : undefined,
  };
};

const ExternalUnmutedChannelLayout: React.FC<{
  useCase: AiPromptUseCaseTabConfig;
  canManage: boolean;
}> = ({ useCase, canManage }) => {
  const { t } = useTranslation(['admin']);
  const [activeRailId, setActiveRailId] = useState<ExternalChannelRailItemId>('all');
  const [channelOverrides, setChannelOverrides] = useState<Record<string, boolean>>({});

  const loadChannelOverrides = useCallback(async () => {
    try {
      const items = await getAiExternalChannelOverrides(useCase.id);
      const next: Record<string, boolean> = {};
      items.forEach((item: AiExternalChannelOverrideStatus) => {
        if (item.channelKey) {
          next[item.channelKey] = item.isCustomized;
        }
      });
      setChannelOverrides(next);
    } catch {
      setChannelOverrides({});
    }
  }, [useCase.id]);

  useEffect(() => {
    void loadChannelOverrides();
  }, [loadChannelOverrides]);

  const activeItem = AI_EXTERNAL_CHANNEL_RAIL_ITEMS.find((item) => item.id === activeRailId)
    ?? AI_EXTERNAL_CHANNEL_RAIL_ITEMS[0];
  const activeUseCase = resolveExternalChannelRailUseCase(useCase, activeItem);

  return (
    <div className={styles.channelConfigLayout}>
      <nav className={styles.channelRail} aria-label={t('aiSettings.externalChannels.navigation', { ns: 'admin' })}>
        {AI_EXTERNAL_CHANNEL_RAIL_ITEMS.map((item) => {
          const label = t(getExternalChannelRailLabelKey(item), { ns: 'admin' });
          const hintKey = getExternalChannelRailHintKey(item);
          const isCustomized = item.inheritsFromAllChannels && Boolean(channelOverrides[item.id]);

          return (
            <button
              key={item.id}
              type="button"
              className={`${styles.channelRailButton} ${item.nested ? styles.channelRailButtonNested : ''} ${activeRailId === item.id ? styles.channelRailButtonActive : ''}`}
              aria-current={activeRailId === item.id ? 'page' : undefined}
              onClick={() => setActiveRailId(item.id)}
            >
              <span className={styles.channelRailMain}>
                {item.iconKind ? (
                  <ConversationChannelBadge
                    icon={{
                      key: item.id,
                      kind: item.iconKind,
                      label,
                    }}
                    title={label}
                    compact
                    size="compact"
                    disableTooltip
                  />
                ) : null}
                <span className={styles.channelRailLabel}>{label}</span>
              </span>
              {hintKey ? (
                <span className={styles.channelRailHint}>{t(hintKey, { ns: 'admin' })}</span>
              ) : null}
              {isCustomized ? (
                <span className={styles.channelCustomBadge}>{t('aiSettings.externalChannels.customized', { ns: 'admin' })}</span>
              ) : null}
            </button>
          );
        })}
      </nav>
      <div className={styles.channelConfigBody}>
        <PromptEditorTab
          key={activeUseCase.id}
          useCase={activeUseCase}
          canManage={canManage}
          onOverrideStateChange={() => void loadChannelOverrides()}
        />
      </div>
    </div>
  );
};

export const AiSettingsPage: React.FC = () => {
  const { t } = useTranslation(['admin']);
  const { hasPermission } = useAuth();
  const canManage = hasPermission([PermissionCodes.aiConfigManage, PermissionCodes.knowledgeManage]);
  const [activeTab, setActiveTab] = useState<AiSettingsTabId>(AI_SETTINGS_GENERAL_TAB.id);
  const activeUseCase = AI_PROMPT_USE_CASE_TABS.find((tab) => tab.id === activeTab) ?? AI_PROMPT_USE_CASE_TABS[0];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('aiSettings.pageTitle', { ns: 'admin' })}</h1>
        <p className={styles.pageSubtitle}>
          {t('aiSettings.pageSubtitle', { ns: 'admin' })}
        </p>
      </div>

      <div className={styles.tabs}>
        <button
          key={AI_SETTINGS_GENERAL_TAB.id}
          className={`${styles.tab} ${activeTab === AI_SETTINGS_GENERAL_TAB.id ? styles.active : ''}`}
          onClick={() => setActiveTab(AI_SETTINGS_GENERAL_TAB.id)}
        >
          {t('aiSettings.general.tabLabel', { ns: 'admin', defaultValue: AI_SETTINGS_GENERAL_TAB.tabLabel })}
        </button>
        {AI_PROMPT_USE_CASE_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(`${getUseCaseTranslationPrefix(tab.id)}.tabLabel`, { ns: 'admin', defaultValue: tab.tabLabel })}
          </button>
        ))}
      </div>

      <div className={styles.tabBody}>
        {activeTab === AI_SETTINGS_GENERAL_TAB.id ? (
          <AiGeneralSettingsTab canManage={canManage} />
        ) : activeUseCase.id === AiPromptUseCase.ReplyExternalUnmuted ? (
          <ExternalUnmutedChannelLayout
            useCase={activeUseCase}
            canManage={canManage}
          />
        ) : (
          <PromptEditorTab
            key={activeTab}
            useCase={activeUseCase}
            canManage={canManage}
          />
        )}
      </div>
    </div>
  );
};

export default AiSettingsPage;
